import React, { useState, useRef, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RotateCcw,
  Keyboard,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

interface TicketValidationResult {
  valid: boolean;
  alreadyUsed: boolean;
  ticketCode: string;
  ticketType: string;
  participantName: string;
  participantSurname: string;
  participantBirthDate: string;
  checkedInAt?: string;
  message: string;
}

const TicketScanner: React.FC = () => {
  const { currentUser, userProfile, loading: authLoading } = useAuth();

  const [scanResult, setScanResult] = useState<TicketValidationResult | null>(
    null,
  );
  const [scanning, setScanning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const scannerRef = useRef<any>(null);
  const scannerContainerId = "qr-reader";

  const isAdmin = userProfile?.ruolo === "admin";
  const isCollaboratore = userProfile?.ruolo === "collaboratore";
  const isAdminOrCollaboratore = isAdmin || isCollaboratore;

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (e) {
        // ignore
      }
      scannerRef.current = null;
    }
    setScanning(false);
    setScannerReady(false);
  }, []);

  const handleScan = useCallback(
    async (decodedText: string) => {
      if (verifying || checkingIn) return;

      // Stop scanner immediately
      await stopScanner();

      // Verify the ticket
      setVerifying(true);
      setCameraError(null);
      try {
        const verifyTicketFn = httpsCallable<
          { ticketCode: string },
          TicketValidationResult
        >(functions, "verifyTicket");
        const result = await verifyTicketFn({ ticketCode: decodedText });
        setScanResult(result.data);
      } catch (err: any) {
        console.error("Verify error:", err);
        setScanResult({
          valid: false,
          alreadyUsed: false,
          ticketCode: decodedText,
          ticketType: "",
          participantName: "",
          participantSurname: "",
          participantBirthDate: "",
          message: "Errore durante la verifica. Riprova.",
        });
      } finally {
        setVerifying(false);
      }
    },
    [verifying, checkingIn, stopScanner],
  );

  const startScanner = useCallback(async () => {
    setScanResult(null);
    setScanning(true);
    setShowManualInput(false);
    setCameraError(null);

    // Dynamically import to avoid bundle issues
    const { Html5Qrcode } = await import("html5-qrcode");

    // Small delay to ensure DOM element is rendered
    await new Promise((r) => setTimeout(r, 200));

    const html5QrCode = new Html5Qrcode(scannerContainerId);
    scannerRef.current = html5QrCode;

    try {
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText) => {
          handleScan(decodedText);
        },
        () => {
          // ignore scan failures (no QR found yet)
        },
      );
      setScannerReady(true);
    } catch (err: any) {
      console.error("Scanner start error:", err);
      setScanning(false);
      setScannerReady(false);
      setCameraError(
        typeof err === "string"
          ? err
          : "Impossibile accedere alla fotocamera. Verifica i permessi del browser.",
      );
    }
  }, [handleScan]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  const handleCheckIn = async (ticketCode: string) => {
    setCheckingIn(true);
    try {
      const checkInFn = httpsCallable<
        { ticketCode: string },
        TicketValidationResult
      >(functions, "checkInTicket");
      const result = await checkInFn({ ticketCode });
      setScanResult(result.data);
    } catch (err) {
      console.error("Check-in error:", err);
      setScanResult((prev) =>
        prev
          ? { ...prev, message: "Errore durante il check-in. Riprova." }
          : null,
      );
    } finally {
      setCheckingIn(false);
    }
  };

  const handleManualVerify = async () => {
    if (!manualCode.trim()) return;
    await handleScan(manualCode.trim());
    setManualCode("");
  };

  const resetScanner = () => {
    setScanResult(null);
    setVerifying(false);
    setCheckingIn(false);
    setCameraError(null);
  };

  // Auth guard
  if (!authLoading && (!currentUser || !isAdminOrCollaboratore)) {
    return <Navigate to="/" replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-romanian-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom max-w-lg mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-romanian-blue/10 rounded-full mb-4">
            <Camera className="w-8 h-8 text-romanian-blue" />
          </div>
          <h1 className="font-display text-3xl font-bold text-romanian-blue mb-2">
            Scanner Biglietti
          </h1>
          <p className="text-gray-600">
            Scansiona il QR code del biglietto per verificare e registrare
            l'ingresso.
          </p>
        </div>

        {/* Camera Error */}
        {cameraError && !scanning && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-700 text-sm font-medium">
                Errore Fotocamera
              </p>
              <p className="text-red-600 text-sm mt-1">{cameraError}</p>
            </div>
          </div>
        )}

        {/* Scanner Area */}
        {!scanResult && !verifying && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            {scanning ? (
              <div>
                <div
                  id={scannerContainerId}
                  className="rounded-lg overflow-hidden mb-4"
                  style={{ minHeight: 300 }}
                />
                {!scannerReady && (
                  <div className="flex items-center justify-center gap-2 text-gray-500 py-4">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Avvio fotocamera...</span>
                  </div>
                )}
                <button
                  onClick={stopScanner}
                  className="w-full py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors mt-2"
                >
                  Ferma Scanner
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={startScanner}
                  className="w-full flex items-center justify-center gap-3 py-4 text-lg bg-romanian-blue text-white rounded-lg hover:bg-romanian-blue/90 transition-colors font-semibold"
                >
                  <Camera className="w-6 h-6" />
                  Avvia Scanner QR
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">oppure</span>
                  </div>
                </div>

                <button
                  onClick={() => setShowManualInput(!showManualInput)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Keyboard className="w-5 h-5" />
                  Inserisci codice manualmente
                </button>

                {showManualInput && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleManualVerify()
                      }
                      placeholder="Es: 1234-5678-9012"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romanian-blue focus:border-transparent outline-none"
                    />
                    <button
                      onClick={handleManualVerify}
                      disabled={!manualCode.trim()}
                      className="px-6 py-2 bg-romanian-blue text-white rounded-lg hover:bg-romanian-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Verifica
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {verifying && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-romanian-blue mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Verifica in corso...</p>
          </div>
        )}

        {/* Result */}
        {scanResult && !verifying && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            {/* Status Icon */}
            <div className="text-center mb-6">
              {scanResult.valid && !scanResult.alreadyUsed ? (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-3">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              ) : scanResult.alreadyUsed ? (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-3">
                  <AlertTriangle className="w-12 h-12 text-yellow-500" />
                </div>
              ) : (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-3">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
              )}
              <p
                className={`text-lg font-semibold ${
                  scanResult.valid && !scanResult.alreadyUsed
                    ? "text-green-700"
                    : scanResult.alreadyUsed
                      ? "text-yellow-700"
                      : "text-red-700"
                }`}
              >
                {scanResult.message}
              </p>
            </div>

            {/* Ticket Details */}
            {(scanResult.participantName || scanResult.ticketCode) && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Codice</span>
                  <span className="font-mono font-semibold text-romanian-blue">
                    {scanResult.ticketCode}
                  </span>
                </div>
                {scanResult.participantName && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Partecipante</span>
                    <span className="font-semibold">
                      {scanResult.participantName}{" "}
                      {scanResult.participantSurname}
                    </span>
                  </div>
                )}
                {scanResult.participantBirthDate && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">
                      Data di nascita
                    </span>
                    <span>{scanResult.participantBirthDate}</span>
                  </div>
                )}
                {scanResult.ticketType && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Tipo</span>
                    <span
                      className={`uppercase text-xs font-bold px-3 py-1 rounded-full ${
                        scanResult.ticketType === "vip"
                          ? "bg-yellow-100 text-yellow-800"
                          : scanResult.ticketType === "child"
                            ? "bg-green-100 text-green-800"
                            : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {scanResult.ticketType === "vip"
                        ? "VIP"
                        : scanResult.ticketType === "child"
                          ? "Bambino"
                          : "Spettatore"}
                    </span>
                  </div>
                )}
                {scanResult.checkedInAt && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">
                      Check-in effettuato
                    </span>
                    <span className="text-yellow-600 font-medium">
                      {scanResult.checkedInAt}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {scanResult.valid && !scanResult.alreadyUsed && (
                <button
                  onClick={() => handleCheckIn(scanResult.ticketCode)}
                  disabled={checkingIn}
                  className="w-full flex items-center justify-center gap-2 py-3 text-lg bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingIn ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Check-in in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Conferma Check-in
                    </>
                  )}
                </button>
              )}

              <button
                onClick={resetScanner}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                Scansiona un altro biglietto
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketScanner;
